import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Calendar,
  Edit2,
  Save,
  X,
  Users,
  BookUser,
  UserPlus,
} from "lucide-react";

const formatDateSafe = (dateStr, fmt = "MMM d, yyyy") => {
  if (!dateStr) return "-";
  const datePart = dateStr.split("T")[0];
  const [y, m, d] = datePart.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  if (fmt === "MMM d") return `${months[date.getMonth()]} ${date.getDate()}`;
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const formatCurrency = (value) => {
  if (!value) return "-";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value);
};

/* ── Key Dates Card ── */
export const KeyDatesCard = ({ transaction, isEditing, editData, setEditData }) => (
  <Card className="card-surface" data-testid="key-dates-card">
    <CardHeader className="border-b border-[var(--t-border)] py-4 px-5">
      <CardTitle className="text-lg font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)]">Key Dates</CardTitle>
    </CardHeader>
    <CardContent className="p-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Contract", key: "contract_date", testId: "edit-contract-date" },
          { label: "EM Due Date", key: "earnest_money_deadline", testId: "edit-em-due-date" },
          { label: "Inspection", key: "inspection_deadline", testId: "edit-inspection" },
          { label: "Financing", key: "financing_deadline", testId: "edit-financing" },
          { label: "Closing", key: "closing_date", testId: "edit-closing-date" },
        ].map((d) => (
          <div key={d.key}>
            <p className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] font-semibold mb-1">{d.label}</p>
            {isEditing ? (
              <Input
                type="date"
                value={editData[d.key] || ""}
                onChange={(e) => setEditData({ ...editData, [d.key]: e.target.value })}
                className="h-7 text-xs px-1.5"
                data-testid={d.testId}
              />
            ) : (
              <p className="text-sm font-medium text-[var(--t-text)]">
                {transaction[d.key] ? formatDateSafe(transaction[d.key]) : "-"}
              </p>
            )}
          </div>
        ))}
      </div>
      {isEditing && (
        <div className="mt-3">
          <p className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] font-semibold mb-1">Appraisal Deadline</p>
          <Input
            type="date"
            value={editData.appraisal_deadline || ""}
            onChange={(e) => setEditData({ ...editData, appraisal_deadline: e.target.value })}
            className="h-7 text-xs px-1.5 w-40"
            data-testid="edit-appraisal"
          />
        </div>
      )}
    </CardContent>
  </Card>
);

/* ── Financial Card ── */
export const FinancialCard = ({ transaction, isEditing, editData, setEditData }) => (
  <Card className="card-surface" data-testid="financial-card">
    <CardHeader className="border-b border-[var(--t-border)] py-4 px-5">
      <CardTitle className="text-lg font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)]">Financial</CardTitle>
    </CardHeader>
    <CardContent className="p-5 space-y-4">
      <div>
        <p className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] font-semibold mb-1">Purchase Price</p>
        {isEditing ? (
          <Input type="number" value={editData.purchase_price} onChange={(e) => setEditData({ ...editData, purchase_price: e.target.value })} className="h-8 text-sm" />
        ) : (
          <p className="text-lg font-bold text-[var(--t-primary)]">{formatCurrency(transaction.purchase_price)}</p>
        )}
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] font-semibold mb-1">Earnest Money</p>
        {isEditing ? (
          <Input type="number" step="0.01" value={editData.earnest_money} onChange={(e) => setEditData({ ...editData, earnest_money: e.target.value })} className="h-8 text-sm w-32" data-testid="edit-earnest-money" />
        ) : (
          <p className="text-sm font-medium text-[var(--t-text)]">{formatCurrency(transaction.earnest_money)}</p>
        )}
      </div>
    </CardContent>
  </Card>
);

/* ── Status Card ── */
export const StatusCard = ({ transaction, isEditing, editData, setEditData }) => (
  <Card className="card-surface" data-testid="status-card">
    <CardContent className="py-4 px-5">
      {isEditing ? (
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-[var(--t-text)]">Status:</span>
          <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
            <SelectTrigger className="h-8 w-auto" data-testid="edit-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active_listing">Active Listing</SelectItem>
              <SelectItem value="active">Before Inspections</SelectItem>
              <SelectItem value="through_inspections">Through Inspections</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : (
        <p className="text-sm">
          <span className="font-semibold text-[var(--t-text)]">Status: </span>
          <span className="capitalize">
            {transaction.status === "active_listing" ? "Active Listing" : transaction.status === "active" ? "Before Inspections" : (transaction.status === "through_inspections" || transaction.status === "pending") ? "Through Inspections" : transaction.status}
          </span>
        </p>
      )}
    </CardContent>
  </Card>
);

/* ── Coordinator Display (sub-component) ── */
const CoordinatorDisplay = ({ coordinator }) => (
  <div className="px-5 py-2.5 flex items-center gap-4" data-testid="assigned-coordinator-display">
    <div className="min-w-0">
      <p className="text-xs text-[var(--t-text-subtle)] uppercase tracking-wider">Assigned Coordinator</p>
      <p className="text-sm font-medium text-[var(--t-text)]">
        {coordinator ? coordinator.name : <span className="text-[var(--t-text-subtle)] italic">Unassigned</span>}
      </p>
    </div>
    {coordinator?.email && <span className="text-xs text-[var(--t-text-muted)] truncate">{coordinator.email}</span>}
  </div>
);

/* ── People List (sub-component) ── */
const PEOPLE_CONFIG = [
  { label: "Buyer", prefix: "buyer" },
  { label: "Buyer 2", prefix: "buyer2" },
  { label: "Seller", prefix: "seller" },
  { label: "Seller 2", prefix: "seller2" },
  { label: "Buyer's Agent", prefix: "buyer_agent" },
  { label: "Seller's Agent", prefix: "seller_agent" },
  { label: "Seller Agent Assistant", prefix: "seller_agent_assistant" },
  { label: "Lender", nameKey: "lender_name", prefix: "lender", contactIdKey: "lender_contact_id" },
  { label: "Title Company", nameKey: "title_company_name", prefix: "title_company", contactIdKey: "title_company_id" },
  { label: "Home Warranty", nameKey: "home_warranty_company", prefix: "home_warranty" },
];

const PeopleList = ({ transaction, enrichFromContacts }) => {
  const rows = PEOPLE_CONFIG.map((cfg) => {
    const nameKey = cfg.nameKey || `${cfg.prefix}_name`;
    const name = transaction[nameKey] || (cfg.contactIdKey && transaction[cfg.contactIdKey] ? "—" : null);
    return { label: cfg.label, name, email: transaction[`${cfg.prefix}_email`], phone: transaction[`${cfg.prefix}_phone`], prefix: cfg.prefix };
  }).filter((p) => p.name);

  if (rows.length === 0) {
    return <div className="px-5 py-4 text-sm text-[var(--t-text-subtle)]">No people assigned.</div>;
  }

  return rows.map((person) => {
    const match = enrichFromContacts(person.name, null);
    const phone = person.phone || match.phone;
    const email = person.email || match.email;
    return (
      <div key={person.label} className="px-5 py-2.5 flex items-center gap-4" data-testid={`person-${person.label}`}>
        <div className="min-w-0">
          <p className="text-xs text-[var(--t-text-subtle)] uppercase tracking-wider">{person.label}</p>
          <p className="text-sm font-medium text-[var(--t-text)]">{person.name}</p>
        </div>
        {phone && <span className="text-xs text-[var(--t-text-muted)] flex-shrink-0">{phone}</span>}
        {email && <span className="text-xs text-[var(--t-text-muted)] truncate">{email}</span>}
      </div>
    );
  });
};

/* ── People Card ── */

const CONTACT_TYPE_MAP = {
  buyer: "buyer", buyer2: "buyer", seller: "seller", seller2: "seller",
  buyer_agent: "buyer_agent", seller_agent: "seller_agent",
  seller_agent_assistant: "seller_agent", lender: "lender",
  title_company: "title_company", home_warranty: "service_provider",
  loan_processor: "lender",
};

const ContactPicker = ({ contacts, onSelect }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-[var(--t-text-subtle)] hover:text-[var(--t-primary)] hover:bg-[var(--t-primary)]/10 transition-colors"
          title="Pick from contacts"
          data-testid="pick-contact-btn"
        >
          <BookUser className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search contacts..." className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty className="py-4 text-center text-xs text-[var(--t-text-muted)]">No contacts found.</CommandEmpty>
            <CommandGroup>
              {contacts.filter(c => c.name).map((c) => (
                <CommandItem
                  key={c.contact_id}
                  onSelect={() => { onSelect(c); setOpen(false); }}
                  className="text-xs cursor-pointer"
                  data-testid={`pick-contact-${c.contact_id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.name}</p>
                    {c.email && <p className="text-[10px] text-[var(--t-text-muted)] truncate">{c.email}</p>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const SaveAsContactBtn = ({ name, email, phone, prefix, contacts = [] }) => {
  const [saving, setSaving] = useState(false);

  const doSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/contacts`, {
        name: name.trim(),
        email: email?.trim() || null,
        phone: phone?.trim() || null,
        contact_type: CONTACT_TYPE_MAP[prefix] || "other",
      }, { withCredentials: true });
      toast.success(`"${name.trim()}" saved to contacts`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to save contact";
      toast.error(typeof msg === "string" ? msg : "Failed to save contact");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!name?.trim()) return;
    const emailLower = (email || "").toLowerCase().trim();
    if (emailLower) {
      const dup = contacts.find(c => (c.email || "").toLowerCase().trim() === emailLower);
      if (dup) {
        toast.warning(`A contact with "${emailLower}" already exists`, {
          description: `Existing: ${dup.name || "Unnamed"}${dup.phone ? ` · ${dup.phone}` : ""}`,
          action: { label: "Save Anyway", onClick: doSave },
          duration: 8000,
        });
        return;
      }
    }
    doSave();
  };

  if (!name?.trim()) return null;
  return (
    <button
      onClick={handleSave}
      disabled={saving}
      className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-[var(--t-text-subtle)] hover:text-green-600 hover:bg-green-50 transition-colors disabled:opacity-40"
      title="Save as contact"
      data-testid={`save-as-contact-${prefix}`}
    >
      <UserPlus className="w-3.5 h-3.5" />
    </button>
  );
};

export const PeopleCard = ({ transaction, isEditing, activeUsers, contacts, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState({});

  const enrichFromContacts = (name, contactId) => {
    let contact = null;
    if (contactId) contact = contacts.find((c) => c.contact_id === contactId);
    if (!contact && name) contact = contacts.find((c) => c.name?.toLowerCase() === name?.toLowerCase());
    return contact || {};
  };

  const startEdit = () => {
    const enrich = (name, cid) => enrichFromContacts(name, cid);
    const buyerC = enrich(transaction.buyer_name);
    const sellerC = enrich(transaction.seller_name);
    const baC = enrich(transaction.buyer_agent_name);
    const saC = enrich(transaction.seller_agent_name);
    const lC = enrich(transaction.lender_name, transaction.lender_contact_id);
    const tcC = enrich(transaction.title_company_name, transaction.title_company_id);
    const hwC = enrich(transaction.home_warranty_company);
    setData({
      assigned_coordinator_id: transaction.assigned_coordinator_id || "",
      buyer_name: transaction.buyer_name || "", buyer_email: transaction.buyer_email || buyerC.email || "", buyer_phone: transaction.buyer_phone || buyerC.phone || "",
      seller_name: transaction.seller_name || "", seller_email: transaction.seller_email || sellerC.email || "", seller_phone: transaction.seller_phone || sellerC.phone || "",
      buyer_agent_name: transaction.buyer_agent_name || "", buyer_agent_email: transaction.buyer_agent_email || baC.email || "", buyer_agent_phone: transaction.buyer_agent_phone || baC.phone || "",
      seller_agent_name: transaction.seller_agent_name || "", seller_agent_email: transaction.seller_agent_email || saC.email || "", seller_agent_phone: transaction.seller_agent_phone || saC.phone || "",
      seller_agent_assistant_name: transaction.seller_agent_assistant_name || "", seller_agent_assistant_email: transaction.seller_agent_assistant_email || "", seller_agent_assistant_phone: transaction.seller_agent_assistant_phone || "",
      buyer2_name: transaction.buyer2_name || "", buyer2_email: transaction.buyer2_email || "", buyer2_phone: transaction.buyer2_phone || "",
      seller2_name: transaction.seller2_name || "", seller2_email: transaction.seller2_email || "", seller2_phone: transaction.seller2_phone || "",
      lender_name: transaction.lender_name || lC.name || "", lender_phone: transaction.lender_phone || lC.phone || "", lender_email: transaction.lender_email || lC.email || "",
      title_company_name: transaction.title_company_name || tcC.name || "", title_company_phone: transaction.title_company_phone || tcC.phone || "", title_company_email: transaction.title_company_email || tcC.email || "",
      home_warranty_company: transaction.home_warranty_company || "", home_warranty_phone: transaction.home_warranty_phone || hwC.phone || "", home_warranty_email: transaction.home_warranty_email || hwC.email || "",
    });
    setEditing(true);
  };

  const handleSave = async () => {
    await onSave(data);
    setEditing(false);
  };

  const PEOPLE_ROWS = [
    { label: "Buyer", prefix: "buyer" },
    { label: "Buyer 2", prefix: "buyer2" },
    { label: "Seller", prefix: "seller" },
    { label: "Seller 2", prefix: "seller2" },
    { label: "Buyer's Agent", prefix: "buyer_agent" },
    { label: "Seller's Agent", prefix: "seller_agent" },
    { label: "Seller Agent Assistant", prefix: "seller_agent_assistant" },
    { label: "Lender", prefix: "lender", nameField: "lender_name" },
    { label: "Title Company", prefix: "title_company", nameField: "title_company_name" },
    { label: "Home Warranty", prefix: "home_warranty", nameField: "home_warranty_company" },
  ];

  return (
    <Card className="card-surface" data-testid="people-card">
      <CardHeader className="border-b border-[var(--t-border)] py-3 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)] uppercase tracking-wider">People</CardTitle>
          {!isEditing && !editing && (
            <Button variant="ghost" size="sm" onClick={startEdit} className="text-[var(--t-text-subtle)] hover:text-[var(--t-text-muted)] h-7" data-testid="edit-people-btn">
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0 divide-y divide-slate-100">
        {editing ? (
          <div className="p-4 space-y-3">
            <div>
              <p className="text-xs text-[var(--t-text-subtle)] uppercase tracking-wider mb-1">Assigned Coordinator</p>
              <Select value={data.assigned_coordinator_id || "unassigned"} onValueChange={(val) => setData({ ...data, assigned_coordinator_id: val === "unassigned" ? "" : val })}>
                <SelectTrigger className="h-8 text-xs" data-testid="edit-assigned-coordinator"><SelectValue placeholder="Select coordinator..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {activeUsers.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role === "super_admin" ? "Admin" : "Coordinator"})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {PEOPLE_ROWS.map((row) => {
              const nk = row.nameField || `${row.prefix}_name`;
              const pk = `${row.prefix}_phone`;
              const ek = `${row.prefix}_email`;
              const handlePickContact = (contact) => {
                setData({
                  ...data,
                  [nk]: contact.name || data[nk] || "",
                  [pk]: contact.phone || data[pk] || "",
                  [ek]: contact.email || data[ek] || "",
                });
              };
              return (
                <div key={row.prefix}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <p className="text-xs text-[var(--t-text-subtle)] uppercase tracking-wider">{row.label}</p>
                    <ContactPicker contacts={contacts} onSelect={handlePickContact} />
                    <SaveAsContactBtn
                      name={data[nk]}
                      email={data[ek]}
                      phone={data[pk]}
                      prefix={row.prefix}
                      contacts={contacts}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Name" value={data[nk] || ""} onChange={(e) => setData({ ...data, [nk]: e.target.value })} className="h-7 text-xs" data-testid={`edit-${row.prefix}-name`} />
                    <Input placeholder="Phone" value={data[pk] || ""} onChange={(e) => setData({ ...data, [pk]: e.target.value })} className="h-7 text-xs" data-testid={`edit-${row.prefix}-phone`} />
                    <Input placeholder="Email" value={data[ek] || ""} onChange={(e) => setData({ ...data, [ek]: e.target.value })} className="h-7 text-xs" data-testid={`edit-${row.prefix}-email`} />
                  </div>
                </div>
              );
            })}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="cancel-people-edit">Cancel</Button>
              <Button size="sm" onClick={handleSave} className="bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]" data-testid="save-people-edit">Save</Button>
            </div>
          </div>
        ) : (
          <>
            <CoordinatorDisplay coordinator={activeUsers.find((u) => u.user_id === transaction.assigned_coordinator_id)} />
            <PeopleList transaction={transaction} contacts={contacts} enrichFromContacts={enrichFromContacts} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

/* ── Notes Card ── */
export const NotesCard = ({ transaction, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const handleSave = async () => {
    await onSave(value);
    setEditing(false);
  };

  return (
    <Card className="card-surface" data-testid="notes-card">
      <CardHeader className="border-b border-[var(--t-border)] py-3 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)] uppercase tracking-wider">Notes</CardTitle>
          {!editing && (
            <Button variant="ghost" size="sm" onClick={() => { setEditing(true); setValue(transaction.notes || ""); }} className="text-[var(--t-text-subtle)] hover:text-[var(--t-text-muted)] h-7" data-testid="edit-notes-btn">
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {editing ? (
          <div className="space-y-3">
            <Textarea value={value} onChange={(e) => setValue(e.target.value)} rows={6} data-testid="edit-notes" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="cancel-notes-edit">Cancel</Button>
              <Button size="sm" onClick={handleSave} className="bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]" data-testid="save-notes-edit">Save</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[var(--t-text-muted)] whitespace-pre-line">{transaction.notes || "No notes added."}</p>
        )}
      </CardContent>
    </Card>
  );
};

/* ── Activity Log Card ── */
export const ActivityLogCard = ({ transaction }) => {
  const { Plus, Edit2, CheckCircle2, RefreshCw, Send, Calendar, FileText, Clock, Activity } = require("lucide-react");

  const formatTs = (timestamp) => {
    if (!timestamp) return "";
    try {
      const d = new Date(timestamp);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
        " at " +
        d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    } catch { return ""; }
  };

  const iconMap = {
    transaction_created: <Plus className="w-3.5 h-3.5 text-green-600" />,
    transaction_edited: <Edit2 className="w-3.5 h-3.5 text-blue-600" />,
    task_added: <Plus className="w-3.5 h-3.5 text-indigo-600" />,
    task_completed: <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />,
    task_uncompleted: <RefreshCw className="w-3.5 h-3.5 text-amber-600" />,
    task_edited: <Edit2 className="w-3.5 h-3.5 text-[var(--t-text-muted)]" />,
    email_sent: <Send className="w-3.5 h-3.5 text-blue-600" />,
    dates_changed: <Calendar className="w-3.5 h-3.5 text-orange-600" />,
    workflow_applied: <FileText className="w-3.5 h-3.5 text-purple-600" />,
  };

  return (
    <Card className="card-surface" data-testid="activity-log-card">
      <CardHeader className="border-b border-[var(--t-border)] py-3 px-5">
        <CardTitle className="text-sm font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)] uppercase tracking-wider flex items-center gap-2">
          <Activity className="w-4 h-4" /> Activity Log
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {transaction.activity_log && transaction.activity_log.length > 0 ? (
          <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-50">
            {[...transaction.activity_log].reverse().map((entry, i) => {
              const icon = iconMap[entry.event_type] || <Clock className="w-3.5 h-3.5 text-[var(--t-text-subtle)]" />;
              let ts = formatTs(entry.timestamp);
              return (
                <div key={entry.log_id || i} className="px-4 py-3 flex gap-3" data-testid={`activity-${i}`}>
                  <div className="mt-0.5 flex-shrink-0">{icon}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-[var(--t-text)] leading-relaxed">{entry.description}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[11px] text-[var(--t-text-subtle)]">{entry.user_name}</span>
                      {ts && (
                        <>
                          <span className="text-[11px] text-[var(--t-text-subtle)]">&middot;</span>
                          <span className="text-[11px] text-[var(--t-text-muted)] flex items-center gap-1" data-testid={`activity-timestamp-${i}`}>
                            <Clock className="w-3 h-3" />{ts}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-6 text-center text-sm text-[var(--t-text-subtle)]">No activity recorded yet.</div>
        )}
      </CardContent>
    </Card>
  );
};
